import { FC, memo } from 'react';
import isEqual from 'lodash/fp/isEqual';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { standardizeUrl, Team, TeamPhoto } from '@frx/frx-services';
import { Card, DonationProgress } from '../../../../components';
import { DefaultTeamImg, External } from '../../../../assets';
import { getDataTracking, TeamMemberProps } from '../../../../utils';
import { LeaveTeamModal } from './LeaveTeamModal';

export interface YourTeamProps {
  teamInfo: Team | undefined;
  teamMembers: TeamMemberProps[] | undefined;
  teamPhoto: TeamPhoto | undefined;
  showCompanyName?: boolean;
  showTeamProgress?: boolean;
  showLeaveTeam?: boolean;
}

export const YourTeam: FC<YourTeamProps> = memo(
  ({
    teamInfo,
    teamMembers,
    teamPhoto,
    showCompanyName = false,
    showTeamProgress = true,
    showLeaveTeam = true,
  }: YourTeamProps) => {
    const percentRaised = Math.round(
      (Number(teamInfo?.amountRaised) / Number(teamInfo?.goal)) * 100
    );

    const captainScreenname =
      teamInfo?.captainScreenname || teamInfo?.captainFirstName;

    return (
      <Card mb={6}>
        <Heading size="md" color="brand.primary.swatch1">
          Your Team
        </Heading>

        <VStack mt={4} spacing={5} w="full">
          <Box>
            <Image
              borderRadius="4px"
              boxSize="96px"
              objectFit="cover"
              src={standardizeUrl(String(teamPhoto?.customUrl), true)}
              fallbackSrc={DefaultTeamImg}
              alt={teamInfo?.name}
            />
          </Box>
          <VStack spacing={2} w="full">
            <Text fontSize="1.5rem">{teamInfo?.name}</Text>
            <HStack>
              <AvatarGroup size="md" max={5}>
                {(teamMembers || []).map((member, index) => (
                  <Avatar
                    name={member.memberDisplayName}
                    src={member.photoUrl}
                    key={index}
                  />
                ))}
              </AvatarGroup>
            </HStack>
          </VStack>

          <Flex justifyContent="space-around" width="full" textAlign="center">
            <Box>
              <Text color="brand.additional.black" fontSize="1.5rem">
                {teamInfo?.numMembers}
              </Text>
              <Text color="brand.monochromatic.600" fontSize="sm">
                Members
              </Text>
            </Box>
          </Flex>

          {!teamInfo?.isCaptainAnon && (
            <VStack spacing={1} w="full">
              <Text color="brand.monochromatic.600" fontSize="sm">
                Team Captain
              </Text>
              <Text color="brand.additional.black" fontWeight="semibold">
                {captainScreenname}
              </Text>
            </VStack>
          )}

          {showCompanyName && (
            <VStack spacing={1} w="full">
              <Text color="brand.monochromatic.600" fontSize="sm">
                Company
              </Text>
              <Text color="brand.additional.black" fontWeight="semibold">
                {teamInfo?.companyName}
              </Text>
            </VStack>
          )}

          <VStack spacing={2} w="full" alignItems="left">
            {showTeamProgress && (
              <Flex width="full" mb={4}>
                <DonationProgress
                  goal={Number(teamInfo?.goal) || 0}
                  raised={Number(teamInfo?.amountRaised) || 0}
                  isTeam
                />

                <Flex alignItems="center">
                  <CircularProgress
                    capIsRound
                    color={
                      Number(percentRaised) > 99
                        ? 'brand.additional.success'
                        : 'brand.primary.swatch1'
                    }
                    size="4rem"
                    thickness="1rem"
                    value={percentRaised || 0}
                  >
                    <CircularProgressLabel fontWeight="bold">
                      {percentRaised || 0}%
                    </CircularProgressLabel>
                  </CircularProgress>
                </Flex>
              </Flex>
            )}
          </VStack>
          <VStack w="full">
            {showLeaveTeam && (
              <Button
                as="a"
                href={teamInfo?.teamPageURL}
                type="button"
                colorScheme="blue"
                variant="secondary"
                textDecoration="none"
                leftIcon={<External />}
                w="full"
                {...getDataTracking('viewTeamPageButton')}
              >
                View Team Page
              </Button>
            )}
            {!showLeaveTeam && (
              <Link
                href={teamInfo?.teamPageURL}
                textDecoration="none"
                _hover={{
                  textDecoration: 'none',
                }}
                _visited={{
                  color: 'brand.additional.link',
                }}
                {...getDataTracking('viewTeamPageLink')}
              >
                <Text fontSize="md">View Team Page &gt;</Text>
              </Link>
            )}
            {showLeaveTeam && <LeaveTeamModal />}
          </VStack>
        </VStack>
      </Card>
    );
  },
  isEqual
);

YourTeam.displayName = 'YourTeam';
